import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Mail, Loader2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n";
import { useAuth } from "../auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("farlight_chat_session");
  if (!id) {
    id = `chat_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("farlight_chat_session", id);
  }
  return id;
};

// Assistente chat disattivato su richiesta della titolare (#288235): nessun widget, nessun consumo di crediti.
const ASSISTANT_ENABLED = false;

export const AssistantWidget = () => {
  const { t, lang } = useLanguage();
  const a = t.assistant;
  const { user, loading: authLoading } = useAuth();
  const guest = !authLoading && !user;
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("assistant") === "1");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [escalate, setEscalate] = useState(false);
  const [service, setService] = useState(null);
  const endRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/assistant/history/${sessionId.current}`).then((res) => {
      setMessages(res.data);
      setEscalate(res.data.some((m) => m.escalate));
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/assistant/chat`, { session_id: sessionId.current, message: msg, lang });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
      if (res.data.escalate) { setEscalate(true); setService(res.data.service || null); }
    } catch (err) {
      if (err?.response?.status === 401) {
        setMessages((m) => [...m, { role: "assistant", content: a.loginRequired }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", content: a.error }]);
        setEscalate(true);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!ASSISTANT_ENABLED) return null; // placeholder
  return (
    <>
      {!open && (
        <button
          data-testid="assistant-open-button"
          onClick={() => setOpen(true)}
          aria-label={a.launcher || a.open}
          title={a.launcher || a.open}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex items-center gap-3 bg-ink text-cream hover:bg-espresso shadow-[0_16px_40px_rgba(26,24,23,0.35)] rounded-full pl-4 pr-5 h-12 sm:h-14 transition-transform hover:-translate-y-0.5"
        >
          <span className="relative">
            <MessageCircle className="w-6 h-6" strokeWidth={1.5} />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amberl" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-[0.15em]">{a.launcherShort || a.launcher || a.title}</span>
        </button>
      )}

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[min(600px,calc(100vh-6rem))] bg-white border border-ink/10 rounded-sm shadow-[0_32px_80px_rgba(26,24,23,0.3)] flex flex-col overflow-hidden"
        >
          <div className="bg-espresso text-cream px-5 py-4 flex items-start justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 font-display text-xl font-medium">
                <Sparkles className="w-4 h-4 text-amberl" strokeWidth={1.5} />
                {a.title}
              </p>
              <p className="text-xs text-cream/60 mt-0.5">{a.subtitle}</p>
            </div>
            <button data-testid="assistant-close-button" onClick={() => setOpen(false)} aria-label={a.close} className="text-cream/70 hover:text-cream">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-cream">
            <div className="bg-white border border-ink/10 rounded-sm px-4 py-3 text-sm leading-relaxed text-ink max-w-[90%]">{a.welcome}</div>
            {messages.length === 0 && !guest && (
              <div className="flex flex-wrap gap-2 pt-1">
                {a.suggestions.map((s, i) => (
                  <button key={i} data-testid={`assistant-suggestion-${i}`} onClick={() => send(s)} className="text-xs text-ink border border-ink/20 hover:border-amberdark hover:text-amberdark rounded-full px-3 py-1.5 transition-colors text-left">
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`assistant-message-${m.role}-${i}`}
                className={`rounded-sm px-4 py-3 text-sm leading-relaxed max-w-[90%] whitespace-pre-line ${m.role === "user" ? "ml-auto bg-ink text-cream" : "bg-white border border-ink/10 text-ink"}`}
              >
                {m.content}
              </div>
            ))}
            {busy && (
              <div data-testid="assistant-thinking" className="flex items-center gap-2 text-xs text-inksoft">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-amberdark" />
                {a.thinking}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {escalate && (
            <div data-testid="assistant-escalation" className="bg-[#F7F5F0] border-t border-[#C5A059]/40 px-5 py-3 text-xs leading-relaxed text-ink space-y-2">
              <p className="font-semibold">{service === "esecutivo" ? a.suggestExec : a.suggestConcept}</p>
              <div className="flex flex-wrap gap-2">
                <a data-testid="assistant-link-concept" href="/richiedi-concept?plan=base" className={`inline-flex items-center rounded-sm px-3 h-8 font-semibold uppercase tracking-[0.12em] text-[10px] ${service !== "esecutivo" ? "bg-ink text-cream" : "border border-ink/30 text-ink"}`}>Visual &amp; Light Rendering Concept</a>
                <a data-testid="assistant-link-esecutivo" href="/richiedi-concept?plan=advanced" className={`inline-flex items-center rounded-sm px-3 h-8 font-semibold uppercase tracking-[0.12em] text-[10px] ${service === "esecutivo" ? "bg-ink text-cream" : "border border-ink/30 text-ink"}`}>Consulenza Esecutiva</a>
              </div>
            </div>
          )}

          {guest ? (
            <div data-testid="assistant-login-required" className="border-t border-ink/10 p-4 bg-white text-sm text-ink space-y-3">
              <p>{a.loginRequired}</p>
              <Link to="/login" data-testid="assistant-login-link" className="inline-flex items-center rounded-sm bg-ink text-cream hover:bg-espresso px-5 h-10 text-xs font-semibold uppercase tracking-[0.15em]">{a.loginCta}</Link>
            </div>
          ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-ink/10 p-3 bg-white"
          >
            <div className="flex items-center gap-2">
            <input
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={a.placeholder}
              className="flex-1 h-11 px-3 text-sm bg-cream border border-ink/10 rounded-sm focus:outline-none focus:ring-1 focus:ring-amberl"
            />
            <button
              type="submit"
              data-testid="assistant-send-button"
              disabled={busy || !input.trim()}
              aria-label={a.send}
              className="h-11 w-11 flex items-center justify-center bg-ink text-cream hover:bg-espresso disabled:opacity-40 rounded-sm transition-colors"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
            </div>
          </form>
          )}
        </div>
      )}
    </>
  );
};
